/**
 * Outil « Corde et tangente » (Boîte à outils, Tle → prépa).
 *
 * Deux points A et B sur la courbe (au doigt ou aux flèches) : la tangente
 * en A et la corde [AB]. Sur une zone convexe (f″ ≥ 0, bande verte sous
 * l'axe), la corde est au-dessus de la courbe et la courbe au-dessus de ses
 * tangentes ; sur une zone concave, le contraire. Au point d'inflexion I,
 * f″ change de signe et la tangente traverse la courbe. Inégalités de
 * convexité classiques (eˣ ≥ 1 + x, ln x ≤ x − 1). Calculs : `convexity.ts`.
 */

import { useRef, useState, type KeyboardEvent } from 'react';
import { MathText, cn, sampleFunctionPath } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, Segmented } from '../../ui/controls';
import { makeFrame } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { DragHandle, FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  chordPosition,
  fnById,
  FUNCS,
  inflectionsBetween,
  tangentPosition,
  zoneAt,
  type FnId,
} from './convexity';

const SIZE = 320;
const CURVE = 'var(--color-primary-500)';
const TANGENT = 'var(--color-accent-700)';
const CHORD = 'var(--color-ink-900)';
const CONVEX = 'var(--color-success-600)';
const CONCAVE = 'var(--color-error-500)';
const START: Record<FnId, [number, number]> = {
  square: [-1, 2],
  exp: [0, 1.5],
  ln: [1, 4],
  cubic: [-1.5, 1.5],
  sin: [-2.5, 1],
};

const num = (v: number) => formatNumber(v, 2);
const snap = (v: number) => Math.round(v * 10) / 10;
/** abscisse d'inflexion : « π », « −π », « 0 » */
const xName = (v: number) =>
  Math.abs(Math.abs(v) - Math.PI) < 1e-9 ? (v < 0 ? '−π' : 'π') : num(v);
const KEY_DIR: Record<string, 1 | -1> = {
  ArrowRight: 1,
  ArrowUp: 1,
  ArrowLeft: -1,
  ArrowDown: -1,
};

export function ChordTangentTool(_props: ToolProps) {
  const [id, setId] = useState<FnId>('exp');
  const fn = fnById(id);
  const frame = makeFrame(fn.vp, SIZE);
  const [xs, setXs] = useState<{ A: number; B: number }>({ A: START.exp[0], B: START.exp[1] });
  const svgRef = useRef<SVGSVGElement>(null);

  const moveTo = (n: 'A' | 'B', raw: number) =>
    setXs((old) => {
      const v = Math.min(fn.xMax, Math.max(fn.xMin, snap(raw)));
      const other = old[n === 'A' ? 'B' : 'A'];
      return v === other ? old : { ...old, [n]: v };
    });
  const drag = useDrag<'A' | 'B'>({
    svgRef,
    frame,
    ids: ['A', 'B'],
    pointOf: (n) => ({ x: xs[n], y: fn.f(xs[n]) }),
    onDrag: (n, p) => moveTo(n, p.x),
  });
  // au clavier, le point glisse le long de la courbe (↑ comme →, ↓ comme ←)
  // et saute par-dessus l'autre point au lieu de rester bloqué
  const keyProps = (n: 'A' | 'B') => ({
    tabIndex: 0,
    onKeyDown: (e: KeyboardEvent<SVGGElement>) => {
      const dir = KEY_DIR[e.key];
      if (!dir) return;
      e.preventDefault();
      setXs((old) => {
        const other = old[n === 'A' ? 'B' : 'A'];
        let v = snap(old[n] + dir * 0.1);
        if (v === other) v = snap(v + dir * 0.1);
        if (v < fn.xMin - 1e-9 || v > fn.xMax + 1e-9) return old;
        return { ...old, [n]: v };
      });
    },
  });

  const choose = (next: FnId) => {
    setId(next);
    setXs({ A: START[next][0], B: START[next][1] });
  };

  const a = xs.A;
  const b = xs.B;
  const fa = fn.f(a);
  const fb = fn.f(b);
  const slope = fn.d1(a);
  const d2a = fn.d2(a);
  const chord = chordPosition(fn, a, b);
  const tan = tangentPosition(fn, a);
  const zone = zoneAt(fn, a);
  const between = inflectionsBetween(fn, a, b);
  const zoneText =
    zone === 'inflection'
      ? ': f″ s’annule et change de signe, A est un point d’inflexion.'
      : zone === 'convex'
        ? '≥ 0 : la fonction est convexe autour de A.'
        : '≤ 0 : la fonction est concave autour de A.';
  const tangentText =
    tan === 'curveAbove'
      ? 'la courbe est au-dessus sur toute la fenêtre.'
      : tan === 'curveBelow'
        ? 'la courbe est en dessous sur toute la fenêtre.'
        : zone === 'inflection'
          ? 'elle traverse la courbe en A.'
          : 'elle recoupe la courbe plus loin : la convexité change en chemin.';
  const chordText =
    chord === 'above'
      ? 'au-dessus de la courbe entre A et B.'
      : chord === 'below'
        ? 'en dessous de la courbe entre A et B.'
        : between.length > 0
          ? `elle traverse la courbe : la fonction change de convexité entre A et B (inflexion en x = ${between.map(xName).join(', en x = ')}).`
          : 'elle traverse la courbe.';
  const P = (x: number, y: number) => ({ x: frame.tx(x), y: frame.ty(y) });
  const pa = P(a, fa);
  const pb = P(b, fb);
  const tanAt = (x: number) => fa + slope * (x - a);
  const axisY = Math.min(SIZE - 6, Math.max(6, frame.ty(0)));

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Fonction"
        value={id}
        options={FUNCS.map((f) => ({ value: f.id, label: f.label }))}
        onChange={choose}
      />

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Courbe de f(x) = ${fn.label}, tangente en A et corde [AB].`}
          data-testid="ct-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} />
          {/* zones convexe / concave le long de l'axe des abscisses */}
          {fn.zones.map((z, i) => {
            const x1 = frame.tx(Math.max(z.from, fn.vp.xMin));
            const x2 = frame.tx(Math.min(z.to, fn.vp.xMax));
            return (
              <rect
                key={i}
                x={x1}
                y={axisY - 3}
                width={Math.max(0, x2 - x1)}
                height={6}
                fill={z.kind === 'convex' ? CONVEX : CONCAVE}
                fillOpacity={0.55}
              />
            );
          })}
          <path
            d={sampleFunctionPath(fn.f, [fn.vp.xMin, fn.vp.xMax], frame)}
            fill="none"
            stroke={CURVE}
            strokeWidth={3}
          />
          <line
            x1={frame.tx(fn.vp.xMin)}
            y1={frame.ty(tanAt(fn.vp.xMin))}
            x2={frame.tx(fn.vp.xMax)}
            y2={frame.ty(tanAt(fn.vp.xMax))}
            stroke={TANGENT}
            strokeWidth={2}
          />
          <line
            x1={pa.x}
            y1={pa.y}
            x2={pb.x}
            y2={pb.y}
            stroke={CHORD}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
          {fn.inflections.map((v) => {
            const p = P(v, fn.f(v));
            return (
              <g key={v}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={5}
                  fill="var(--fig-bg, #fff)"
                  stroke={CHORD}
                  strokeWidth={2}
                />
                <HaloText x={p.x + 8} y={p.y + 16} fontSize={14} fontWeight={700} fill={CHORD}>
                  I
                </HaloText>
              </g>
            );
          })}
          {(['A', 'B'] as const).map((n) => {
            const p = n === 'A' ? pa : pb;
            return (
              <DragHandle
                key={n}
                x={p.x}
                y={p.y}
                color={n === 'A' ? TANGENT : CHORD}
                label={n}
                labelDx={p.x > SIZE - 24 ? -14 : 12}
                labelDy={p.y < 24 ? 16 : -14}
                name={`Point ${n} d’abscisse ${num(xs[n])}`}
                active={drag.active === n}
                keyProps={keyProps(n)}
              />
            );
          })}
        </svg>
        <p className="mt-1 flex flex-wrap justify-center gap-x-4 text-xs font-semibold">
          <span className="text-success-600">▬ convexe (f″ ≥ 0)</span>
          <span className="text-error-600">▬ concave (f″ ≤ 0)</span>
          {fn.inflections.length > 0 && <span className="text-ink-700">○ I : inflexion</span>}
        </p>
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="ct-info"
      >
        <MathText className="text-ink-900 block overflow-x-auto">{`$f(x) = ${fn.tex}, \\quad ${fn.d2tex}$`}</MathText>
        <p className="text-ink-700 text-sm">
          En A (x = {num(a)}) : f″ = {num(d2a)} {zoneText}
        </p>
        <p className="text-sm" data-testid="ct-tangent" style={{ color: TANGENT }}>
          <strong>Tangente en A</strong> (pente {num(slope)}) : {tangentText}
        </p>
        <p className="text-ink-900 text-sm" data-testid="ct-chord">
          <strong>Corde [AB]</strong> : {chordText}
        </p>
        <p className="sr-only" aria-live="polite">
          A en x = {num(a)}, B en x = {num(b)}. Tangente en A : {tangentText} Corde [AB] :{' '}
          {chordText}
        </p>
      </section>

      <Note live={false}>{fn.inequality}</Note>
    </div>
  );
}
